import { useBalance } from "wagmi";
import { useGetCurrentAccount } from ".";
import { getCryptoAsset, isBitcoinLikeCryptoAsset, mapCryptoAssetToDetails, } from "../utils/cryptoAssets";
import { fromFixedPoint } from "../utils/numbers";
import { useAssetsConversionRates } from "./useAssetsConversionRates";
import useWalletAccount from "./useWalletAccount";
import { useTokensBalances } from "./useTokensBalances";
import { useBorrowData } from "./useBorrowData";
import { CHAIN_ID } from "../constants";
import { usePassportContext } from "./usePassportContext";
import { useWatchTransferEventsForAllTokens } from "./useWatchTransferEvents";
import { convertToUsd } from "../utils/currency";
const DEFAULT_ASSET_DATA = {
    rawBalance: 0n,
    formattedBalance: "0.0000",
    usdBalance: 0,
    formattedUsdBalance: "$0.00",
};
/**
 * Aggregates dropdown data for Dropdown component.
 * @param {accountDataRefetchInterval: number, nativeBalanceRefetchInterval: number }
 * Object containing refetch intervals time (in milliseconds) for account data
 * (like mats or mezo id) and for native balance. After that time the specific
 * value should be marked as stale and re-fetched. Default value for both is
 * 90000 (90 secs).
 * @returns Dropdown data.
 * @dev This hook is for internal use only.
 */
export default function useDropdownData({ accountDataRefetchInterval = 90000, nativeBalanceRefetchInterval = 90000, }) {
    const { walletAddress, accountAddress, networkFamily: walletType, } = useWalletAccount();
    const { environment = "mainnet" } = usePassportContext();
    const { data: btcBalance } = useBalance({
        address: accountAddress,
        chainId: CHAIN_ID[environment],
        query: {
            staleTime: nativeBalanceRefetchInterval,
            refetchInterval: nativeBalanceRefetchInterval,
        },
    });
    const { data: passportAccount } = useGetCurrentAccount({
        staleTime: accountDataRefetchInterval,
        refetchInterval: accountDataRefetchInterval,
        enabled: !!accountAddress,
    });
    const { data: assetsConversionRates } = useAssetsConversionRates();
    const { data: tokensBalances } = useTokensBalances();
    const { data: debt } = useBorrowData();
    useWatchTransferEventsForAllTokens();
    const mezoId = passportAccount?.mezoId;
    const detailedAssets = [
        ["BTC", btcBalance?.value ?? 0n],
        ...Object.entries(tokensBalances ?? {}),
    ].map((asset) => {
        const [type, balance] = asset;
        if (isBitcoinLikeCryptoAsset(type)) {
            return mapCryptoAssetToDetails(type, balance, assetsConversionRates.btc.price, assetsConversionRates.btc.decimals);
        }
        if (type === "mT") {
            return mapCryptoAssetToDetails(type, balance, assetsConversionRates.t.price, assetsConversionRates.t.decimals);
        }
        return mapCryptoAssetToDetails(type, balance, 1n, 0);
    });
    const btcData = detailedAssets.find(({ type }) => type === "BTC");
    const musdData = detailedAssets.find(({ type }) => type === "MUSD");
    const otherAssetsData = detailedAssets.filter(({ type }) => !["BTC", "MUSD"].includes(type));
    const otherAssetsCount = otherAssetsData.filter((asset) => asset.rawBalance !== 0n).length;
    const assets = {
        btc: btcData || {
            type: "BTC",
            ...DEFAULT_ASSET_DATA,
        },
        musd: musdData || {
            type: "mUSD",
            ...DEFAULT_ASSET_DATA,
        },
    };
    const otherAssetsUsdTotal = otherAssetsData.reduce((sum, { usdBalance }) => sum + usdBalance, 0);
    const matsBalance = passportAccount?.mats.totalMats || 0;
    const usdCollateral = convertToUsd(debt?.collateral ?? 0n, getCryptoAsset("MUSD").decimals, assetsConversionRates.btc.price, assetsConversionRates.btc.decimals);
    const usdTroveDebt = fromFixedPoint(debt?.troveDebt ?? 0n, 18);
    const usdTotalBalance = detailedAssets.reduce((sum, { usdBalance }) => sum + usdBalance, 0) +
        usdCollateral;
    return {
        mezoId,
        matsBalance,
        accountAddress,
        walletAddress,
        walletType,
        usdTotalBalance,
        usdTroveDebt,
        usdCollateral,
        assets,
        otherAssetsCount,
        otherAssetsUsdTotal,
    };
}
//# sourceMappingURL=useDropdownData.js.map